/**
 * OAuth callback page — the provider redirects here after the user consents.
 *
 * URL shape: /oauth/{provider}/callback?code=…&state=…
 *
 * Responsibilities:
 *   1. Parse ``code`` + ``state`` from the query string.
 *   2. Verify ``state`` matches the value we stored in sessionStorage before
 *      the redirect (CSRF defence — the backend re-checks signature only).
 *   3. Forward ``code`` + ``state`` to the backend, which validates the
 *      id_token, links / creates the user, and returns JWTs.
 *   4. Navigate to /home (or /onboarding if the user is brand-new).
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { completeOAuth, consumeStoredOAuthState } from '../api/oauth'

export default function OAuthCallbackPage() {
  const { provider = 'google' } = useParams<{ provider?: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code = params.get('code')
    const state = params.get('state')
    const providerError = params.get('error')

    if (providerError) {
      setError(`Sign-in was cancelled (${providerError}).`)
      return
    }

    if (!code || !state) {
      setError('Sign-in callback was missing a required parameter.')
      return
    }

    const expected = consumeStoredOAuthState(provider)
    if (expected && expected !== state) {
      setError('Sign-in state mismatch — please try again.')
      return
    }

    let cancelled = false
    completeOAuth(provider, code, state)
      .then(({ requiresOnboarding }) => {
        if (cancelled) return
        navigate(requiresOnboarding ? '/onboarding' : '/home', { replace: true })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const detail = (err as { response?: { data?: { detail?: unknown } } })
          ?.response?.data?.detail
        const msg =
          typeof detail === 'string'
            ? detail
            : typeof (detail as { detail?: unknown })?.detail === 'string'
              ? ((detail as { detail: string }).detail)
              : 'Sign-in failed. Please try again.'
        setError(msg)
      })
    return () => { cancelled = true }
  }, [provider, params, navigate])

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-page)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          maxWidth: 400,
          width: '100%',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 32,
          textAlign: 'center',
        }}
      >
        {error ? (
          <>
            <p style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 12 }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              style={{
                marginTop: 8,
                padding: '10px 16px',
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                backgroundColor: 'var(--accent-fill)',
                color: 'var(--text-on-accent)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
              }}
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>Signing you in…</p>
        )}
      </div>
    </div>
  )
}
